import {
  FILE_TYPES,
  IClientProjectData,
  IFileData,
  ISampleFile
} from "@/features/clientProjectDetails";
import { clientProjectThunk } from "@/reducers/thunks/clientProject";
import { APIResponse } from "@/types/apiResponse";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";

const initialState = {
  projectName: "",
  targetLocales: [],
  qualityCheckEnabled: undefined,
  lipSyncEnabled: false,
  currentStep: 0,
  numberOfVideos: 0,
  duplicateFrom: undefined,
  isCreatingProject: false,
  selectedSampleFiles: null,
  uploadedSampleFileIds: []

  // projectName: "New Project 12",
  // sourceLocale: "ENGLISH",
  // targetLocales: [],
  // qualityCheckEnabled: true,
  // lipSyncEnabled: true,
  // currentStep: 1,
  // projectId: "P017001365395614K6"

  // projectName: "New Project 13",
  // sourceLocale: "ENGLISH",
  // targetLocales: ["HINDI", "FRENCH", "ITALIAN"],
  // qualityCheckEnabled: true,
  // lipSyncEnabled: true,
  // currentStep: 4,
  // projectId: "P017002088857001TR",
  // numberOfVideos: 4
} as IClientProjectData;

const clientProjectPopupSlice = createSlice({
  name: "clientProjectPopup",
  initialState,
  reducers: {
    incrementStep: (state, action) => {
      state.currentStep += action.payload;
      if (state.currentStep < 0) {
        state.currentStep = 0;
      }
    },
    setDraftState: (state, action) => {
      state.draftState = action.payload;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setProjectName: (state, action) => {
      state.projectName = action.payload;
    },
    setProjectId: (state, action) => {
      state.projectId = action.payload;
    },
    setSourceLanguage: (state, action) => {
      state.sourceLocale = action.payload;
    },
    setDestinationLanguage: (state, action) => {
      state.targetLocales = action.payload;
    },
    updateDestinationLanguage: (state, action) => {
      if (
        state.targetLocales &&
        !state.targetLocales.includes(action.payload)
      ) {
        state.targetLocales = [...state.targetLocales, action.payload];
      }
    },
    removeDestinationLanguage: (state, action) => {
      const targetLocales = state.targetLocales ? [...state.targetLocales] : [];
      const index = targetLocales.indexOf(action.payload);
      if (index > -1) {
        targetLocales.splice(index, 1);
      }

      state.targetLocales = targetLocales;
    },
    toggleLipSync: (state) => {
      state.lipSyncEnabled = !state.lipSyncEnabled;
    },
    setLipSync: (state, action) => {
      state.lipSyncEnabled = action.payload;
    },
    setQualityCheck: (state, action) => {
      state.qualityCheckEnabled = action.payload;
    },
    toggleQualityCheck: (state) => {
      state.qualityCheckEnabled = !state.qualityCheckEnabled;
    },
    putInitialState: (state, action) => {
      state = { ...action.payload };
      return state;
    },
    setNumberOfVideos: (state, action) => {
      state.numberOfVideos = action.payload;
    },
    updateSourceFileResponseList: (
      state,
      action: PayloadAction<Partial<IFileData>>
    ) => {
      const { sourceFileId, ...newData } = action.payload;
      if (sourceFileId && state.sourceFileResponseList?.[sourceFileId]) {
        state.sourceFileResponseList[sourceFileId] = {
          ...state.sourceFileResponseList[sourceFileId],
          ...newData
        };
      }
    },
    resetState: () => {
      return initialState;
    },
    removeFileFromPopUp: (
      state,
      action: PayloadAction<{
        fileId: string;
        projectId: string;
      }>
    ) => {
      const { fileId, projectId } = action.payload;
      if (fileId?.length && projectId?.length) {
        if (state.projectId === projectId) {
          delete state.sourceFileResponseList?.[fileId];
        }
      }
    },
    setDuplicateFromProjectId: (state, action: PayloadAction<string>) => {
      state.duplicateFrom = action.payload;
    },
    setIsCreatingProject: (state, action: PayloadAction<boolean>) => {
      state.isCreatingProject = action.payload;
    },
    setSelectedSampleFile: (state, action: PayloadAction<ISampleFile>) => {
      const newFile = action.payload;

      if (
        state.selectedSampleFiles?.some((f) => f.fileId === newFile?.fileId)
      ) {
        state.selectedSampleFiles = state.selectedSampleFiles.filter(
          (file) => file.fileId !== newFile?.fileId
        );
      } else {
        if (state.selectedSampleFiles) {
          state.selectedSampleFiles = [
            ...(state.selectedSampleFiles || []),
            newFile
          ];
        } else {
          state.selectedSampleFiles = [newFile];
        }
      }
    },
    setSelectedSampleFilesBulk: (
      state,
      action: PayloadAction<ISampleFile[]>
    ) => {
      const selectedFiles = action.payload;

      state.selectedSampleFiles = selectedFiles;
    },
    setUploadedSampleFileIds: (state, action: PayloadAction<string[]>) => {
      state.uploadedSampleFileIds = action.payload;
    },
    removeUploadedSampleFileId: (state, action: PayloadAction<string>) => {
      const fileId = action.payload;
      state.uploadedSampleFileIds = state.uploadedSampleFileIds?.filter(
        (id) => id !== fileId
      );
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(clientProjectThunk.createProject.fulfilled, (state, action) => {
        const project = action.payload?.data.responseData;
        state = {
          ...state,
          ...project
        };
        return state;
      })
      .addCase(clientProjectThunk.updateProject.fulfilled, (state, action) => {
        const project = action.payload?.data.responseData;
        if (state.projectId === project.projectId) {
          state = {
            ...state,
            ...project
          };
        }
        return state;
      })
      .addCase(
        clientProjectThunk.duplicateProject.fulfilled,
        (state, action) => {
          const project = action.payload?.data.responseData.project;
          state = {
            ...state,
            ...project,
            draftState: "UPLOAD_FILES"
          };
          return state;
        }
      )
      .addCase(clientProjectThunk.useSampleFilesInProject.pending, (state) => {
        if (state.selectedSampleFiles?.length) {
          state.selectedSampleFiles = null;
        }
      })
      .addCase(
        clientProjectThunk.useSampleFilesInProject.fulfilled,
        (
          state,
          action: PayloadAction<AxiosResponse<APIResponse<IFileData>>>
        ) => {
          const sourceFiles: ISampleFile[] = (action as any).meta?.arg
            ?.sampleFiles;
          const sampleToSourceFileIdMap = (
            action.payload.data.responseData as any
          )?.sampleToSourceFileIdMap;
          const projectId = (action as any).meta?.arg?.projectId;
          state.uploadedSampleFileIds = [
            ...(state.uploadedSampleFileIds || []),
            ...sourceFiles.map((file) => file.fileId)
          ];
          if (projectId && state.projectId === projectId && sourceFiles) {
            sourceFiles.forEach((sourceFile) => {
              const srcFileId = sampleToSourceFileIdMap[sourceFile.fileId];
              state.sourceFileResponseList = {
                ...(state.sourceFileResponseList || {}),
                [srcFileId]: {
                  originalFileName: sourceFile.fileName,
                  sourceFileId: srcFileId,
                  thumbUrl: sourceFile.thumbnailUrl ?? null,
                  uploadedAt: new Date(),
                  videoUrl: sourceFile.url,
                  fileStatus: "UPLOADED",
                  type: FILE_TYPES.VIDEO,
                  contentType: sourceFile.contentType,
                  fileName: sourceFile.fileName,
                  duration: 0,
                  fileType: FILE_TYPES.VIDEO,
                  dubResponseList: [],
                  archived: false,
                  sourceSampleFileId: sourceFile.fileId
                }
              };
            });
            if (state.selectedSampleFiles?.length) {
              state.selectedSampleFiles = null;
            }
          }
        }
      );
  }
});

export const {
  setProjectName,
  setDraftState,
  setSourceLanguage,
  setDestinationLanguage,
  updateDestinationLanguage,
  toggleLipSync,
  setQualityCheck,
  toggleQualityCheck,
  incrementStep,
  setCurrentStep,
  setProjectId,
  putInitialState,
  setNumberOfVideos,
  removeDestinationLanguage,
  updateSourceFileResponseList,
  resetState,
  removeFileFromPopUp,
  setLipSync,
  setDuplicateFromProjectId,
  setIsCreatingProject,
  setSelectedSampleFile,
  setSelectedSampleFilesBulk,
  setUploadedSampleFileIds,
  removeUploadedSampleFileId
} = clientProjectPopupSlice.actions;

export default clientProjectPopupSlice.reducer;
